import { Component, Host, Prop, h } from '@stencil/core';
import { parseJsonProp } from '../../../../../utils/helpers/hydration';

const COMPONENT_NAME = 'breadcrumbs-type-2';

/**
 * Dark-background breadcrumb navigation rendering an ordered path from root to current page.
 * Single horizontal `<ol>` row with configurable icon separators between items; the last item renders as plain text. Designed to sit directly below `sn-hero-header-type-2` and share its dark navy background for visual continuity.
 * Dark-on-light counterpart to `sn-breadcrumbs-type-1` — adds the navy hero-matched background and an optional `transparent` mode for embedding inside an already-dark section.
 *
 * @source breadcrumb
 * @version 2.0.0
 * @category SignNow
 * @example
 *   <sn-breadcrumbs-type-2 items='[{"label": "Home", "href": "/"}, {"label": "Features", "href": "/features"}, {"label": "eSignature"}]'></sn-breadcrumbs-type-2>
 */
@Component({
  tag: 'sn-breadcrumbs-type-2',
  styleUrl: 'index.scss',
  shadow: false,
})
export class SNBreadcrumbsType2 {
  /**
   * Breadcrumb navigation items displayed in order from root to current page.
   * @content
   * @type {array}
   * @item.type {object}
   * @item.property {string} label - Visible breadcrumb text (e.g. "Home", "Features")
   * @item.property {string} href - Navigation URL. Omit for the last (current page) item to render it as plain text.
   * @item.required label
   * @item.content label
   * @minItems 1
   */
  @Prop({ mutable: true }) items?: any[] | string;

  /**
   * Color mode for theming (drives the [data-color-mode] scope that defines semantic CSS tokens).
   */
  @Prop() colorMode: 'signnow' | 'inverted' = 'signnow';

  /**
   * Icon name for separator between breadcrumb items.
   * @default "arrows--chevron-right"
   */
  @Prop() separator: string = 'arrows--chevron-right';

  /**
   * Add schema.org BreadcrumbList microdata markup.
   * @default true
   */
  @Prop() withSchemaOrgEntities: boolean = true;

  /**
   * Remove the dark navy background — use when the component sits
   * inside a section that already provides its own background (e.g. a dark hero header).
   * @default false
   */
  @Prop() transparent: boolean = false;

  /**
   * Use the deepest navy background (#000031, --color-surface-darkest) to match `sn-hero-header-type-7`.
   * @default false
   */
  @Prop() darkest: boolean = false;

  private parsedItems: any[] = [];

  componentWillLoad() {
    const parsed = parseJsonProp(this.items, []);
    this.parsedItems = Array.isArray(parsed) ? parsed : [];
  }

  render() {
    const items = this.parsedItems;
    if (!items || items.length === 0) return null;

    return (
      <Host
        data-color-mode={this.colorMode}
        class={[this.transparent ? `${COMPONENT_NAME}--transparent` : '', this.darkest ? `${COMPONENT_NAME}--darkest` : ''].filter(Boolean).join(' ')}
      >
        {/* Own section + container instead of me-section: me-section always renders a
            nested me-container, and this block is above the fold, so that dependency
            put the whole layout class matrix into critical CSS. The outer element keeps
            spanning the viewport — it carries the dark background. */}
        <section class={`${COMPONENT_NAME}__section`}>
          <div class={`${COMPONENT_NAME}__container`}>
            <nav
              class={COMPONENT_NAME}
              aria-label="Breadcrumb"
              role="navigation"
              {...(this.withSchemaOrgEntities ? { itemscope: true, itemtype: 'https://schema.org/BreadcrumbList' } : {})}
            >
              <ol class={`${COMPONENT_NAME}__list`}>
                {items.map((item, index) => {
                  const isLast = index === items.length - 1;
                  return (
                    <li
                      class={`${COMPONENT_NAME}__item${isLast ? ` ${COMPONENT_NAME}__item--current` : ''}`}
                      {...(this.withSchemaOrgEntities ? { itemscope: true, itemtype: 'https://schema.org/ListItem', itemprop: 'itemListElement' } : {})}
                    >
                      {item.href && !isLast ? (
                        <a class={`${COMPONENT_NAME}__link`} href={item.href} aria-label={item.label} {...(this.withSchemaOrgEntities ? { itemprop: 'item' } : {})}>
                          <span {...(this.withSchemaOrgEntities ? { itemprop: 'name' } : {})}>{item.label}</span>
                        </a>
                      ) : (
                        <span class={`${COMPONENT_NAME}__text`} aria-current={isLast ? 'page' : undefined} {...(this.withSchemaOrgEntities ? { itemprop: 'name' } : {})}>
                          {item.label}
                        </span>
                      )}
                      {this.withSchemaOrgEntities && isLast && item.href && <meta itemprop="item" content={item.href} />}
                      {!isLast && (
                        <span class={`${COMPONENT_NAME}__separator`} aria-hidden="true">
                          <me-icon name={this.separator} inline />
                        </span>
                      )}
                      {this.withSchemaOrgEntities && <meta itemprop="position" content={String(index + 1)} />}
                    </li>
                  );
                })}
              </ol>
            </nav>
          </div>
        </section>
      </Host>
    );
  }
}
